/* Product information views for the global product template. */
(() => {
  const labels = [
    ['movement', /^(?:movement|movimiento)\s*:\s*(.*)$/i],
    ['caseSize', /^(?:case size|tamaño de caja|dimensiones de caja)\s*:\s*(.*)$/i],
    ['caseMaterial', /^(?:case material|material de caja)\s*:\s*(.*)$/i],
    ['case', /^(?:case|caja)\s*:\s*(.*)$/i],
    ['crystal', /^(?:crystal|cristal)\s*:\s*(.*)$/i],
    ['strap', /^(?:bracelet\s*\/\s*strap|strap\s*\/\s*bracelet|bracelet|strap|correa|brazalete)\s*:\s*(.*)$/i],
    ['additionalBracelets', /^(?:additional bracelets|brazaletes adicionales)\s*:\s*(.*)$/i],
    ['waterResistance', /^(?:water resistance|resistencia al agua)\s*:\s*(.*)$/i],
    ['display', /^(?:display|pantalla)\s*:\s*(.*)$/i],
    ['weight', /^(?:weight|peso)\s*:\s*(.*)$/i],
    ['connectivity', /^(?:connectivity|conectividad)\s*:\s*(.*)$/i],
    ['powerBattery', /^(?:power\s*\/\s*battery|power source|battery|batería|fuente de energía)\s*:\s*(.*)$/i],
    ['powerReserve', /^(?:power reserve|reserva de marcha)\s*:\s*(.*)$/i],
    ['caliber', /^(?:calib(?:er|re)|calibre del movimiento)\s*:\s*(.*)$/i],
    ['module', /^(?:module|módulo)\s*:\s*(.*)$/i],
    ['jewels', /^(?:jewels|rubies|rubíes)\s*:\s*(.*)$/i],
    ['frequency', /^(?:frequency|vibrations|frecuencia)\s*:\s*(.*)$/i],
    ['bezel', /^(?:bezel|bisel)\s*:\s*(.*)$/i],
    ['crown', /^(?:crown|corona)\s*:\s*(.*)$/i],
    ['features', /^(?:main functions|key features|features|funciones principales)\s*:\s*(.*)$/i],
  ];

  const clean = (value) => (value || '').replace(/\s+/g, ' ').trim();
  const usable = (value) => {
    const text = clean(value);
    return text && !/^(?:0(?:[.,]0+)?|undefined|null|nan|\[\]|\[object object\])$/i.test(text) && !/^gid:\/\//i.test(text);
  };
  const specKey = (value) => clean(value).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const genericValues = new Set([
    'strap', 'bracelet', 'band', 'strap bracelet', 'case', 'material', 'case material',
    'movement', 'crystal', 'connectivity', 'display', 'weight', 'case size',
    'case profile', 'water resistance', 'power', 'battery', 'power battery',
    'caliber', 'module', 'jewels', 'frequency', 'set includes',
  ]);
  const meaningful = (label, value) => usable(value)
    && specKey(value) !== specKey(label)
    && !genericValues.has(specKey(value));

  const descriptionLines = (description) => {
    const lines = [];
    let line = '';
    const flush = () => {
      if (clean(line)) lines.push(clean(line));
      line = '';
    };
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        line += node.textContent;
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const tag = node.tagName;
      if (tag === 'BR') {
        flush();
        return;
      }
      const isBlock = /^(?:P|DIV|LI|H[1-6]|TR|DT|DD)$/i.test(tag);
      if (isBlock) flush();
      node.childNodes.forEach(walk);
      if (isBlock) flush();
    };
    description.childNodes.forEach(walk);
    flush();
    return lines;
  };

  const descriptionSpecs = (description) => {
    const lines = descriptionLines(description);
    const specs = {};
    lines.forEach((line, index) => {
      for (const [key, pattern] of labels) {
        const match = line.match(pattern);
        if (!match) continue;
        const next = lines[index + 1] || '';
        const value = clean(match[1] || (labels.some(([, regex]) => regex.test(next)) ? '' : next));
        if (!specs[key] && usable(value)) specs[key] = value;
        break;
      }
    });
    return specs;
  };

  const dimension = (value) => {
    const number = Number(String(value || '').replace(',', '.').replace(/\s*mm$/i, ''));
    return Number.isFinite(number) && number > 0 ? String(value).trim().replace(/\s*mm$/i, '') : '';
  };

  const caseSize = (data, caseText) => {
    // The generic case_size metafield is a filter bucket; WristMatch dimensions are exact.
    // The copy can qualify exact-looking width data with a range or approximation.
    const measurement = String.raw`\d+(?:[.,]\d+)?(?:\s*[–-]\s*\d+(?:[.,]\d+)?)?`;
    const approxWord = String.raw`(?:approximately|approx\.?|about|around|aproximadamente)`;
    const range = (value) => clean(value).replace(/\s*([–-])\s*/g, '$1');
    const dimensions = caseText?.match(new RegExp(`\\b(${approxWord})\\s+(${measurement}\\s*mm(?:\\s*[×x]\\s*${measurement}\\s*mm){1,2}|${measurement}(?:\\s*[×x]\\s*${measurement}){1,2}\\s*mm)\\b`, 'i'));
    if (dimensions) return `Approx. ${range(dimensions[2]).replace(/\s*mm\b/gi, '').replace(/\s*[x×]\s*/gi, ' × ')} mm`;
    const diameter = caseText?.match(new RegExp(`\\b(${approxWord})?\\s*(${measurement})\\s*mm\\s*(?:in\\s+)?diameter\\b`, 'i'));
    if (diameter) {
      const thickness = caseText.match(new RegExp(`\\b(${approxWord})?\\s*(${measurement})\\s*mm\\s*(?:thick|thickness)\\b`, 'i'));
      const size = `${diameter[1] ? 'Approx. ' : ''}${range(diameter[2])} mm diameter`;
      return thickness ? `${size} · ${thickness[1] ? 'Approx. ' : ''}${range(thickness[2])} mm thick` : size;
    }
    const length = dimension(data.specCaseLength);
    const width = dimension(data.specCaseWidth);
    const thickness = dimension(data.specCaseThickness);
    const round = /^round$/i.test(data.specCaseShape || '');
    if (length && width) return `${[length, width, thickness].filter(Boolean).join(' × ')} mm`;
    if (width && round) return `${width} mm diameter${thickness ? ` · ${thickness} mm thick` : ''}`;
    if (width) return `${width} mm wide${thickness ? ` · ${thickness} mm thick` : ''}`;
    if (length) return `${length} mm long${thickness ? ` · ${thickness} mm thick` : ''}`;
    const exactDimensions = caseText?.match(/\b\d+(?:[.,]\d+)?\s*[×x]\s*\d+(?:[.,]\d+)?(?:\s*[×x]\s*\d+(?:[.,]\d+)?)?\s*mm\b/i);
    if (exactDimensions) return clean(exactDimensions[0]);
    const lengthText = caseText?.match(/\b(\d+(?:[.,]\d+)?)\s*mm\s*(?:long|length)\b/i)?.[1];
    const widthText = caseText?.match(/\b(\d+(?:[.,]\d+)?)\s*mm\s*(?:wide|width)\b/i)?.[1];
    if (lengthText && widthText) return `${lengthText} × ${widthText} mm`;
    const reversedDiameter = caseText?.match(/\bdiameter\D{0,12}(\d+(?:[.,]\d+)?\s*mm)\b/i);
    if (reversedDiameter) return `${clean(reversedDiameter[1]).replace(/\s*mm$/i, '')} mm diameter`;
    if (widthText) return `${widthText} mm wide`;
    return usable(data.specCaseSize) && !/^(?:up to \d+\s*mm|\d+\s*[–-]\s*\d+\s*mm|\d+\s*mm and up)$/i.test(clean(data.specCaseSize)) ? clean(data.specCaseSize) : '';
  };

  const caseMaterial = (data, caseText) => {
    if (usable(data.specCaseMaterial)) return clean(data.specCaseMaterial);
    const match = caseText?.match(/\b(?:(?:rose\s+)?gold[- ]tone|silver[- ]tone|two[- ]tone|black\s+PVD|DLC[- ]coated|PVD[- ]coated)?\s*(?:\d{3,4}L\s+)?(?:stainless steel|metal alloy|bioceramic|titanium|ceramic|resin|polymer|plastic|brass|carbon fiber|carbon fibre|bronze|aluminum|aluminium|metal|acero inoxidable|titanio|cerámica|resina|latón)\b/i);
    return match ? clean(match[0]).replace(/\b[a-z]/g, (letter) => letter.toUpperCase()).replace(/\bPvd\b/g, 'PVD').replace(/\bDlc\b/g, 'DLC').replace(/\bTone\b/g, 'tone') : '';
  };

  const movementText = (value) => {
    const source = clean(value);
    if (!source) return '';
    const swiss = /\bswiss\b/i.test(source) ? 'Swiss ' : '';
    const chronograph = /\bchronograph\b(?![- ]style)/i.test(source) ? ' Chronograph' : '';
    if (/\beco[- ]drive\b/i.test(source)) return `${swiss}Eco-Drive${chronograph}`;
    if (/\bsolar|light[- ]powered\b/i.test(source)) return `${swiss}Solar Quartz${chronograph}`;
    if (/\bautomatic|self[- ]winding\b/i.test(source)) return `${swiss}Automatic Mechanical${chronograph}`;
    if (/\bmechanical|hand[- ]winding\b/i.test(source)) return `${swiss}Mechanical${chronograph}`;
    if (/\bquartz\b/i.test(source)) {
      const display = /\bdigital\b/i.test(source) ? 'Digital ' : (/\banalog\b/i.test(source) && !swiss && !chronograph ? 'Analog ' : '');
      return `${swiss}${display}Quartz${chronograph}`;
    }
    return compact(source.replace(/\b(?:precise|reliable|low[- ]maintenance)\b/gi, ''), /[;,]/);
  };

  const strapText = (value) => {
    const source = clean(value);
    if (!source) return '';
    const type = /\b(strap|bracelet|band)\b/i.exec(source);
    if (!type) return compact(source, /\s+with\s+|[;,]/i);
    const prefix = clean(source.slice(0, type.index));
    const known = prefix.match(/\b((?:(?:(?:rose\s+)?gold[- ]tone|silver[- ]tone|two[- ]tone|black\s+PVD|DLC[- ]coated|PVD[- ]coated)\s+)?(?:(?:bio[- ](?:sourced|based)|recycled|plant[- ]based|vegan|genuine)\s+)?(?:stainless steel|polyurethane|titanium|ceramic|rubber|resin|silicone|leather|textile\s*\/\s*fabric|textile|fabric|nylon|mesh|metal alloy|metal|polymer|plastic|canvas|material))$/i);
    const unrecognized = prefix
      .replace(/^(?:(?:black|blue|brown|grey|gray|green|white|red|pink|purple|sand beige)\s+)+/i, '')
      .replace(/\b(?:flexible|durable|lightweight|comfortable|premium|refined|traditional|classic|integrated|adjustable|soft-touch|genuine)\b/gi, '');
    const technical = clean(known?.[1] || unrecognized);
    const material = technical && technical.split(/\s+/).length <= 5 && !genericValues.has(specKey(technical))
      ? technical.replace(/\bgenuine\s+/gi, '').replace(/\b[a-z]/g, (letter) => letter.toUpperCase()).replace(/\b(?:Tone|Sourced|Based)\b/g, (word) => word.toLowerCase()).replace(/\bPvd\b/g, 'PVD').replace(/\bDlc\b/g, 'DLC')
      : '';
    const closure = source.match(/\b(?:jewelry clasp|jewellery clasp|pin buckle|deployant clasp|deployment clasp|folding clasp|butterfly clasp|tang buckle|hook clasp)\b/i);
    const kind = type[1].toLowerCase() === 'bracelet' ? 'Bracelet' : 'Strap';
    const item = material ? material + ' ' + kind : (closure ? kind : '');
    return `${item}${closure ? ` · ${closure[0].replace(/\b[a-z]/g, (letter) => letter.toUpperCase())}` : ''}`;
  };

  const caseProfile = (caseText, highlights) => {
    const source = `${caseText || ''} ${highlights || ''}`;
    if (/\b(?:ultra[- ](?:thin|slim)|ultra[- ]thin case)\b/i.test(source)) return 'Ultra-slim';
    if (/\bslim\s+(?:profile|case|construction)\b/i.test(caseText || '')) return 'Slim';
    return '';
  };

  const setIncludes = (title, description, fallback) => {
    const opening = descriptionLines(description).slice(0, 4).join(' ');
    const set = /\bwatch\s*(?:&|and|\+)\s*bracelet\s+(?:gift\s+)?set\b/i.test(`${title || ''} ${opening}`);
    return set && /\b(?:matching|coordinated|additional)\s+(?:\w+\s+){0,2}bracelets?\b/i.test(`${opening} ${fallback.additionalBracelets || ''}`)
      ? 'Watch + Coordinated Bracelets' : '';
  };

  const labeledSection = (description, heading) => {
    const lines = descriptionLines(description);
    const start = lines.findIndex((line) => heading.test(line));
    if (start < 0) return '';
    const details = [clean(lines[start].replace(heading, ''))];
    for (let index = start + 1; index < lines.length && details.length < 20; index += 1) {
      if (labels.some(([, pattern]) => pattern.test(lines[index])) || /^[\p{L}][\p{L}\s/–-]{2,45}:/u.test(lines[index])) break;
      details.push(lines[index]);
    }
    return details.filter(Boolean).join(' ');
  };

  const weight = (data, fallback) => {
    if (usable(data.specWeight)) return clean(data.specWeight);
    if (usable(fallback.weight)) return clean(fallback.weight);
    const match = fallback.case?.match(/\b(?:weight\s+(?:of|is)|weighs|weighing|peso\s+de)\s*(approximately|approx\.?|about|aproximadamente)?\s*(\d+(?:[.,]\d+)?(?:\s*[–-]\s*\d+(?:[.,]\d+)?)?\s*(?:g|grams?|oz))\b/i);
    return match ? `${match[1] ? 'Approximately ' : ''}${clean(match[2])}` : '';
  };

  const reserveText = (value) => {
    const before = value?.match(/\b((?:approximately|approximate|about|up to|around)?\s*\d+(?:[.,]\d+)?(?:\s*[–-]\s*\d+(?:[.,]\d+)?)?\s*(?:hours?|days?|months?|h))(?:\s*\([^)]{0,40}\))?\s+(?:of\s+)?power reserve\b/i);
    const after = value?.match(/\bpower reserve\s+(?:of\s+)?((?:approximately|approximate|about|up to|around)?\s*\d+(?:[.,]\d+)?(?:\s*[–-]\s*\d+(?:[.,]\d+)?)?\s*(?:hours?|days?|months?|h))\b/i);
    const amount = clean((before || after)?.[1]);
    return amount ? `${amount.replace(/^approximate\b/i, 'Approximately').replace(/^./, (letter) => letter.toUpperCase())} ${/-\s*(?:hour|day|month)/i.test(amount) ? '' : 'of '}power reserve` : '';
  };

  const powerBattery = (data, fallback) => {
    if (usable(data.specPowerBattery)) return clean(data.specPowerBattery);
    const movement = fallback.movement || '';
    const reserve = usable(data.specPowerReserve)
      ? (/power reserve/i.test(data.specPowerReserve) ? clean(data.specPowerReserve) : `${clean(data.specPowerReserve)} power reserve`)
      : (usable(fallback.powerReserve) ? clean(fallback.powerReserve) : reserveText(movement));
    const solar = /\b(?:solar(?:-powered)?|light-powered|eco-drive)\b/i.test(`${data.specPowerSource || ''} ${movement}`);
    if (solar) return reserve ? `Light-powered · ${reserve}` : 'Light-powered';
    if (reserve) return reserve;
    if (usable(data.specBattery) || usable(data.specBatteryLife)) {
      return [data.specBattery, data.specBatteryLife].filter(usable).map(clean).join(' · ');
    }
    if (usable(fallback.powerBattery)) return clean(fallback.powerBattery);
    const battery = movement.match(/\b(?:CR|SR|LR|BR|R)\d{3,4}[A-Z]{0,2}\b/i)?.[0];
    const duration = String.raw`\d+(?:[.,]\d+)?(?:\s*(?:[–-]|to)\s*\d+(?:[.,]\d+)?)?[\s-]*(?:years?|months?)`;
    const before = movement.match(new RegExp(`\\b((?:(?:approximately|approximate|estimated|about|up to|around)\\s+)?${duration})\\s+(?:of\\s+)?battery life\\b`, 'i'))?.[1];
    const after = movement.match(new RegExp(`\\bbattery\\s+(?:life|lifespan)\\s*(?:of|is)?\\s*((?:(?:approximately|approximate|estimated|about|up to|around)\\s+)?${duration})\\b`, 'i'))?.[1];
    const parenthetical = movement.match(new RegExp(`\\bbattery\\s*\\(\\s*~\\s*(${duration})\\s*\\)`, 'i'))?.[1];
    const years = clean(before || after || parenthetical);
    const approximate = /^(?:approximately|approximate|estimated|about|around)\b/i.test(years) || /\b(?:approximate|estimated)\s+battery\s+(?:life|lifespan)\b/i.test(movement) || Boolean(parenthetical);
    const amount = years.replace(/^(?:approximately|approximate|estimated|about|around)\s+/i, '');
    const life = years ? `${approximate ? 'Approximately ' : ''}${amount}${/-\s*(?:year|month)/i.test(amount) ? ' ' : ' of '}battery life` : '';
    return [battery, life].filter(Boolean).join(' · ');
  };

  const connectivity = (data, fallback, functions) => {
    const source = [data.specConnectivity, fallback.connectivity, fallback.movement, functions].filter(usable).join(' ');
    const bluetooth = /\bbluetooth\b/i.test(source);
    const smartphone = /\b(?:smartphone link|phone connectivity)\b/i.test(source);
    const connections = [];
    if (bluetooth || smartphone) connections.push(bluetooth && smartphone ? 'Bluetooth Smartphone Link' : (bluetooth ? 'Bluetooth' : 'Smartphone Link'));
    if (/\b(?:built-in|integrated|onboard)\s+GPS\b|\bGPS\s+(?:receiver|navigation|tracking)\b/i.test(source) && !/\b(?:smartphone|phone|app)[-\s]+GPS\b/i.test(source)) connections.push('GPS');
    if (/\bWi-Fi\b/i.test(source)) connections.push('Wi-Fi');
    if (/\bNFC\b/i.test(source)) connections.push('NFC');
    return connections.join(' · ');
  };

  const caliberModule = (data, fallback) => {
    if (usable(data.specModule) || usable(fallback.module)) return { label: 'Module', value: clean(data.specModule || fallback.module) };
    if (usable(data.specCaliber) || usable(fallback.caliber)) return { label: 'Caliber', value: clean(data.specCaliber || fallback.caliber) };
    const movement = fallback.movement || '';
    const module = movement.match(/\bmodule\s+(\d{3,5})\b/i)?.[1];
    if (module) return { label: 'Module', value: module };
    const caliber = movement.match(/\bcalib(?:er|re)\s+([A-Z0-9][A-Z0-9./-]{2,16})\b/i)?.[1];
    if (caliber) return { label: 'Caliber', value: caliber };
    const eta = movement.match(/\bETA\s+([A-Z]?\d[A-Z0-9.-]{2,12}(?:\s+[A-Z]{2})?)\b/i)?.[1];
    if (eta) return { label: 'Caliber', value: `ETA ${eta}` };
    const head = movement.split(/\b(?:battery|powered by|power reserve)\b/i)[0];
    const code = head.match(/\b(?=[A-Z0-9./-]*[A-Z])(?=[A-Z0-9./-]*\d)[A-Z0-9]{3,8}(?:\.\d{1,3})?\b/g)?.find((item) => !/^(?:CR|SR|LR|BR|R)\d/i.test(item));
    return code ? { label: 'Caliber', value: code } : null;
  };

  const mechanicalDetails = (data, fallback) => {
    const movement = fallback.movement || '';
    const mechanical = /\b(?:automatic|mechanical|self-winding|hand-winding)\b/i.test(`${data.specMovement || ''} ${movement}`);
    if (!mechanical) return { jewels: '', frequency: '' };
    const jewels = usable(data.specJewels) ? clean(data.specJewels) : (usable(fallback.jewels) ? clean(fallback.jewels) : movement.match(/\b\d{1,3}\s+jewels\b/i)?.[0] || '');
    const frequency = usable(data.specFrequency) ? clean(data.specFrequency) : (usable(fallback.frequency) ? clean(fallback.frequency) : movement.match(/\b\d{1,3}(?:,\d{3})\s*(?:vibrations per hour|vph)\b|\b\d+(?:[.,]\d+)?\s*Hz\b/i)?.[0] || '');
    return { jewels, frequency };
  };

  const keyFeatures = ({ structured, functions, highlights, movement, caseText, bezel, crown, connection, set }) => {
    const source = [functions, highlights, movement, caseText, bezel, crown, structured].filter(usable).join(' ');
    const priorities = [
      ['Step Counter', /\b(?:step counter|pedometer)\b/i],
      ['Accelerometer', /\baccelerometer\b/i],
      ['GMT', /\bGMT\b|\bsecond time zone\b/i],
      ['Moon Phase', /\bmoon[\s-]?phase\b/i],
      ['World Time', /\bworld time\b/i],
      ['Interval Timer', /\b(?:interval.{0,25}timer|multi-interval countdown|configurable intervals)\b/i],
      ['Chronograph', /\bchronograph\b(?![- ]style)/i],
      ['Tachymeter', /\btachymeter\b/i],
      ['COSC Chronometer', /\bCOSC[- ]certified\b/i],
      ['Rotating Bezel', /\b(?:unidirectional|bi-directional|bidirectional|rotating)\s+(?:24-hour\s+)?bezel\b|\b24-hour rotating bezel\b/i],
      ['Screw-Down Crown', /\bscrew[- ]down crown\b/i],
      ['Open Heart', /\bopen[- ]heart\b/i],
      ['Exhibition Caseback', /\b(?:exhibition|transparent|see-through)\s+case\s?back\b/i],
      ['Small Seconds', /\bsmall seconds\b/i],
      ['Shock Resistant', /\bshock[- ]resist(?:ant|ance)\b/i],
      ['LED Illumination', /\b(?:LED illumination|LED backlight|illuminator LED)\b/i],
      ['Stopwatch', /\bstopwatch\b/i],
      ['Lume', /\b(?:luminous|luminescent)\s+(?:hands|markers|indices|coating)\b/i],
      ['Day-Date', /\bday[- ]date\b/i],
      ['Matching Bracelet Set', /\b(?:coordinated|matching) bracelet set\b/i],
    ];
    const selected = priorities.filter(([label, pattern]) => pattern.test(source) && (label !== 'Matching Bracelet Set' || set)).map(([label]) => label);
    if (set && !selected.includes('Matching Bracelet Set')) selected.push('Matching Bracelet Set');
    if (set && /\bcrystal accents\b/i.test(`${functions} ${highlights} ${caseText}`)) selected.push('Crystal Accents');
    if (selected.includes('Step Counter') && selected.includes('Accelerometer')) selected.splice(selected.indexOf('Accelerometer'), 1);
    return selected.filter((label) => !(connection && /^(?:Bluetooth|GPS|Wi-Fi|NFC)$/.test(label))).slice(0, 5);
  };

  const compact = (value, divider) => {
    const text = clean(value);
    if (text.length <= 90) return text;
    const parts = text.split(divider).map(clean).filter(Boolean);
    if (parts.length < 2) return text;
    const firstTwo = `${parts[0]}, ${parts[1]}`;
    return firstTwo.length <= 90 ? firstTwo : parts[0];
  };

  const addRow = (grid, label, value, chips = false) => {
    if (!chips && !meaningful(label, value)) return;
    const row = document.createElement('div');
    row.className = 'mr-product-specs__row';
    const term = document.createElement('dt');
    term.textContent = label;
    const detail = document.createElement('dd');
    if (chips) {
      const items = Array.isArray(value) ? value.slice(0, 5) : [];
      if (!items.length) return;
      row.classList.add('mr-product-specs__row--features');
      items.forEach((item) => {
        const chip = document.createElement('span');
        chip.className = 'mr-product-specs__chip';
        chip.textContent = item;
        detail.append(chip);
      });
      detail.classList.add('mr-product-specs__chips');
    } else {
      detail.textContent = clean(value);
    }
    row.append(term, detail);
    grid.append(row);
  };

  const normalizeWatch = (data, full) => {
    const fallback = descriptionSpecs(full);
    const functions = labeledSection(full, /^(?:main functions|key features|features|funciones principales)\s*:\s*/i);
    const highlights = labeledSection(full, /^(?:design highlights|design features|detalles de diseño)\s*:\s*/i);
    const from = (structured, key) => usable(structured) ? clean(structured) : fallback[key];
    const movement = movementText(usable(data.specMovement) ? data.specMovement : fallback.movement);
    const crystal = from(data.specCrystal, 'crystal');
    const water = from(data.specWaterResistance, 'waterResistance');
    const power = powerBattery(data, fallback);
    const connection = connectivity(data, fallback, functions);
    const mechanism = mechanicalDetails(data, fallback);
    const caliber = caliberModule(data, fallback);
    const profile = caseProfile(fallback.case, highlights);
    const set = setIncludes(data.productTitle, full, fallback);
    const rawStrap = usable(data.specStrap) ? data.specStrap : fallback.strap;
    const display = from(data.specDisplay, 'display') || (/\b(?:ana[- ]?digi|analog[- ]digital)\b/i.test(movement) ? 'Analog-Digital' : (/\bdigital\b/i.test(movement) ? 'Digital' : (/\banalog\b/i.test(movement) ? 'Analog' : '')));
    return {
      movement,
      caliber: caliber?.label === 'Caliber' ? caliber.value : '',
      module: caliber?.label === 'Module' ? caliber.value : '',
      power,
      jewels: mechanism.jewels,
      frequency: mechanism.frequency,
      caseSize: caseSize(data, fallback.caseSize || fallback.case),
      caseMaterial: usable(data.specCaseMaterial) ? clean(data.specCaseMaterial) : (usable(fallback.caseMaterial) ? fallback.caseMaterial : caseMaterial(data, fallback.case)),
      profile,
      crystal,
      water,
      strap: strapText(rawStrap),
      display: /^ana[- ]?digi$/i.test(display) ? 'Analog-Digital' : display,
      set,
      weight: weight(data, fallback),
      connection,
      raw: {
        movement: usable(data.specMovement) ? data.specMovement : fallback.movement,
        caseSize: fallback.caseSize,
        caseMaterial: usable(data.specCaseMaterial) ? data.specCaseMaterial : fallback.caseMaterial,
        crystal: usable(data.specCrystal) ? data.specCrystal : fallback.crystal,
        water: usable(data.specWaterResistance) ? data.specWaterResistance : fallback.waterResistance,
        strap: rawStrap,
        display: usable(data.specDisplay) ? data.specDisplay : fallback.display,
        weight: usable(data.specWeight) ? data.specWeight : fallback.weight,
        connection: usable(data.specConnectivity) ? data.specConnectivity : fallback.connectivity,
        power: usable(data.specPowerBattery) ? data.specPowerBattery : fallback.powerBattery,
      },
      features: keyFeatures({ structured: from(data.specFeatures, 'features'), functions, highlights, movement: fallback.movement, caseText: fallback.case, bezel: fallback.bezel, crown: fallback.crown, connection, set }),
    };
  };

  const quickRows = (specs) => {
    // Keep universal specifications, reserve a final row for features, then add the most relevant optional details.
    const core = ['movement', 'caseSize', 'caseMaterial', 'crystal', 'water', 'strap', 'display', 'set'];
    const mechanical = /\b(?:automatic|mechanical|self-winding|hand-winding)\b/i.test(specs.movement);
    const digital = /\bdigital\b/i.test(specs.display);
    const optional = mechanical
      ? ['caliber', 'power', 'jewels', 'frequency', 'profile', 'weight', 'connection', 'module']
      : digital
        ? ['module', 'power', 'connection', 'weight', 'profile', 'caliber', 'jewels', 'frequency']
        : ['profile', 'caliber', 'power', 'weight', 'connection', 'module', 'jewels', 'frequency'];
    const selected = new Set(core.filter((key) => usable(specs[key])));
    const limit = 11 - selected.size - (specs.features.length ? 1 : 0);
    optional.filter((key) => usable(specs[key])).slice(0, Math.max(0, limit)).forEach((key) => selected.add(key));
    const order = [
      ['movement', 'Movement'], ['caliber', 'Caliber'], ['module', 'Module'],
      ['power', 'Power / Battery'], ['jewels', 'Jewels'], ['frequency', 'Frequency'],
      ['caseSize', 'Case Size'], ['caseMaterial', 'Case Material'], ['profile', 'Case Profile'], ['crystal', 'Crystal'],
      ['water', 'Water Resistance'], ['strap', 'Strap / Bracelet'], ['display', 'Display'],
      ['set', 'Set Includes'], ['weight', 'Weight'], ['connection', 'Connectivity'],
    ];
    const rows = order.filter(([key]) => selected.has(key)).map(([key, label]) => {
      const normalized = specs[key];
      const raw = compact(specs.raw?.[key], /\s+with\s+|[;,]/i);
      return { label, value: meaningful(label, normalized) ? normalized : raw };
    }).filter(({ label, value }) => meaningful(label, value));
    if (specs.features.length) rows.push({ label: 'Key Features', value: specs.features, chips: true });
    return rows;
  };

  const setup = (root) => {
    if (root.dataset.mrSpecsReady === 'true') return;
    const quickTab = root.querySelector('[data-mr-spec-tab="quick"]');
    const fullTab = root.querySelector('[data-mr-spec-tab="full"]');
    const quick = root.querySelector('[data-mr-spec-panel="quick"]');
    const full = root.querySelector('[data-mr-spec-panel="full"]');
    const grid = root.querySelector('[data-mr-spec-grid]');
    if (!quickTab || !fullTab || !quick || !full || !grid) return;

    const specs = normalizeWatch(root.dataset, full);
    quickRows(specs).forEach(({ label, value, chips }) => addRow(grid, label, value, chips));
    root.querySelector('[data-mr-spec-empty]').hidden = Boolean(grid.children.length);

    const select = (view, focus = false) => {
      const showQuick = view === 'quick';
      quick.hidden = !showQuick;
      full.hidden = showQuick;
      quickTab.setAttribute('aria-selected', String(showQuick));
      fullTab.setAttribute('aria-selected', String(!showQuick));
      quickTab.tabIndex = showQuick ? 0 : -1;
      fullTab.tabIndex = showQuick ? -1 : 0;
      if (focus) (showQuick ? quickTab : fullTab).focus();
    };
    quickTab.addEventListener('click', () => select('quick'));
    fullTab.addEventListener('click', () => select('full'));
    [quickTab, fullTab].forEach((tab) => tab.addEventListener('keydown', (event) => {
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        select(event.key === 'ArrowLeft' || event.key === 'Home' ? 'quick' : 'full', true);
      }
    }));
    select('quick');
    root.dataset.mrSpecsReady = 'true';
  };

  const init = (scope = document) => scope.querySelectorAll('[data-mr-product-specs]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
